<script setup lang="ts">
import { ArrowLeft, Code, Home, Settings, Trophy, Tv, Users } from "lucide-vue-next";
import { computed } from "vue";
import { useRoute } from "vue-router/auto";
import { useAuthStore } from "@/stores/auth";
import { useGameStore } from "@/stores/game";
import { useLobbyStore } from "@/stores/lobby";

const emit = defineEmits<{ navigate: [] }>();
const auth = useAuthStore();
const game = useGameStore();
const lobby = useLobbyStore();
const route = useRoute();
const items = computed(() => [
	{ label: "Home", to: "/", icon: Home },
	...(auth.loggedIn ? [{ label: "Friends", to: "/friends", icon: Users }] : []),
	{ label: "History", to: "/replay", icon: Tv },
	{ label: "Leaderboard", to: "/leaderboard", icon: Trophy },
	...(auth.isAdmin ? [{ label: "Debug", to: "/debug", icon: Code }] : [])
]);
const active = (to: string) => (to === "/" ? route.path === "/" : route.path === to || route.path.startsWith(to + "/"));
function fallback(event: Event) {
	const img = event.target as HTMLImageElement;
	if (!img.src.endsWith("/bots/player.svg")) img.src = "/bots/player.svg";
}
</script>

<template>
    <nav class="navigation-content" aria-label="Main navigation">
        <RouterLink v-if="game.myUuid || lobby.lobbyID > 0" to="/" class="destination return-link" @click="emit('navigate')"><ArrowLeft :size="20" />{{ game.myUuid ? 'Return to game' : 'Return to table' }}</RouterLink>
        <div class="destinations">
            <RouterLink v-for="item in items" :key="item.to" :to="item.to" class="destination" :aria-current="active(item.to) ? 'page' : undefined" @click="emit('navigate')">
                <component :is="item.icon" :size="20" /><span>{{ item.label }}</span>
                <span v-if="item.to === '/friends' && auth.profile?.receivedFriendRequests?.length" class="badge">{{ auth.profile.receivedFriendRequests.length }}</span>
            </RouterLink>
        </div>
        <div class="secondary">
            <RouterLink v-if="auth.loggedIn" to="/settings" class="destination" :aria-current="active('/settings') ? 'page' : undefined" @click="emit('navigate')"><Settings :size="20" />Settings</RouterLink>
            <!-- Who is signed in, at the foot. Legal links and Sign out live in Settings. -->
            <RouterLink v-if="auth.profile" to="/profile" class="identity" :aria-current="active('/profile') ? 'page' : undefined" @click="emit('navigate')">
                <img :src="auth.profile.profilePictureUrl || '/bots/player.svg'" alt="" @error="fallback" />
                <span><strong>{{ auth.profile.username }}</strong><small><span class="sr-only">Rating </span>{{ auth.profile.elo }}</small></span>
            </RouterLink>
            <RouterLink v-else to="/login" class="destination" @click="emit('navigate')">Log in</RouterLink>
        </div>
    </nav>
</template>

<style scoped>
.navigation-content { display: flex; flex-direction: column; flex: 1; min-height: 0; gap: 24px; }
.identity { display: flex; align-items: center; gap: 10px; margin-top: 8px; padding: 8px 12px; border-radius: 8px; }
.identity:hover { background: hsl(var(--primary) / .1); }
.identity img { width: 32px; height: 32px; object-fit: contain; border-radius: 6px; flex-shrink: 0; }
.identity span { min-width: 0; } .identity strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; }
.identity small { display: block; font-size: 12px; color: hsl(var(--muted-foreground)); font-variant-numeric: tabular-nums; }
.destinations { display: grid; gap: 4px; }
.destination { display: flex; align-items: center; gap: 12px; min-height: 44px; padding: 10px 12px; border-radius: 8px; font-size: 14px; color: hsl(var(--muted-foreground)); }
.destination:hover, [aria-current=page] { color: hsl(var(--primary)); background: hsl(var(--primary) / .1); }
a:focus-visible { outline: 2px solid hsl(var(--primary)); outline-offset: 2px; }
.secondary { margin-top: auto; padding-top: 16px; }
.badge { margin-left: auto; padding: 0 6px; border-radius: 10px; background: hsl(var(--primary)); color: hsl(var(--primary-foreground)); font-size: 11px; }
.return-link { color: hsl(var(--primary)); }
</style>
